// client/src/features/portal/scope.ts — „which contract am I looking at" · PORTAL-002 phase 2.
//
// A tenant is a party to one contract almost always and to several sometimes — a family that
// rents a flat and a parking space from the same landlord, or one that moved between two of
// the landlord's flats. 04 §6.6 puts a „property/contract selector (when the tenant has
// several)" in the portal header for exactly that case, and D-1173 left the slot marked
// because PORTAL-001 had not built the endpoint that can count. It has, so this is the
// resolution the selector and three of the five screens share.
//
// ── the rule, in one place ─────────────────────────────────────────────────────────────────
// The URL names a contract; if it names one of mine, that is the one. Otherwise the DEFAULT is
// the first ACTIVE contract in the server's own order (`start_date` descending — the most
// recent lease first), and failing that the server's first row whatever its status, so a tenant
// whose lease has ended still opens on something rather than on an empty state.
//
// AN ID THAT IS NOT MINE FALLS BACK TO THE DEFAULT rather than rendering an error. The list is
// already narrowed to the caller's own contracts by `PortalScopeResolver`, so an id outside it
// is a stale link, a shared URL from another tenant, or a typo — none of which is a fault the
// tenant can fix and none of which should replace „Chiria mea" with a problem report. The API
// answers the same shape of question with a 404 and nothing here weakens that: every request
// this client makes still carries the resolved id, and a request for someone else's contract
// is never made because the id never becomes the one in view.

import { useSearch } from '@tanstack/react-router';

import type { PortalContractListItem } from '@/client';
import { fold } from '@/lib/text';

/**
 * The `?contract=` of the URL, read from wherever under `/portal` the caller renders.
 *
 * `strict: false` because the callers are the SHELL and the screens alike, and the shell is
 * mounted by five different routes. The param is on the `/portal` layout route's schema
 * (`search.ts`), so it is in the full search of every one of them.
 *
 * It is the RAW param that travels between screens, never the resolved contract: a link that
 * carried the resolved id would put a GUID in the URL of every tenant who has one contract and
 * never chose anything.
 */
export function usePortalContractParam(): string | undefined {
  return useSearch({ strict: false }).contract;
}

/**
 * The contract in view, or `undefined` when the caller has none at all.
 *
 * @param contracts the caller's own contracts as `GET /api/v1/portal/contracts` ordered them.
 * @param requested the `?contract=` of the URL, when there is one.
 */
export function contractInView(
  contracts: readonly PortalContractListItem[] | undefined,
  requested: string | undefined,
): PortalContractListItem | undefined {
  if (contracts === undefined || contracts.length === 0) return undefined;

  const named = contracts.find((contract) => contract.id === requested);
  if (named !== undefined) return named;

  return contracts.find((contract) => contract.status === 'ACTIVE') ?? contracts[0];
}

/**
 * Whether §6.6's condition for rendering the selector is met — „when the tenant has several".
 *
 * A separate function rather than `contracts.length > 1` at three call sites, because the
 * condition is the document's and the reading of it is one decision: an ended contract still
 * counts, because its rent history and its documents are still the tenant's and still need a
 * way to be reached.
 */
export function hasSeveralContracts(
  contracts: readonly PortalContractListItem[] | undefined,
): boolean {
  return (contracts?.length ?? 0) > 1;
}

/**
 * The one-line label a contract wears in the selector, on „Selectare contract”, in the incident
 * form's sentence and as an incident's place: the property's name, then the unit when the
 * contract names one — „Casă Băneasa · Apartament parter”.
 *
 * The NAME first since D-4021. The label used to lead with the address, on the reasoning that
 * the street is how a tenant tells two flats apart; the tester of the 2026-10-20 round answered
 * it (TC-083): a renter is told the property's name by the landlord, „Casă Băneasa”, and a
 * picker that printed only „Str. Nicolae Caramfil 22, București” never connected the two. The
 * street and the city are still shown, as a second line under the label wherever a row has
 * room for one (`contractAddress`). The name is 03 §7.2's NOT NULL column, so a label always
 * has it. The contract number is never part of it — it is the landlord's filing reference and
 * it is on the detail, not in a picker.
 */
export function contractLabel(contract: PortalContractListItem): string {
  return contract.unitName == null
    ? contract.propertyName
    : `${contract.propertyName} · ${contract.unitName}`;
}

/**
 * The street and the city a picker prints under `contractLabel` (D-4021), or `undefined` when
 * there is no second line to print: the property carries no address, or its name already is
 * the address — a landlord who named the flat „Str. Aviatorilor 8, ap. 2, București” would
 * otherwise see it twice. Compared through the search boxes' `fold`, without case or
 * diacritics, as a reader would compare them: Romanian collation keeps „ș” apart from „s”.
 */
export function contractAddress(contract: PortalContractListItem): string | undefined {
  const address = contract.propertyAddress?.trim();
  if (address === undefined || address === '') return undefined;
  return fold(address) === fold(contract.propertyName.trim()) ? undefined : address;
}
